import { FormGroup, FormParagraph, List } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { IOcrkV1DiagnosticReportCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/ocrkV1DiagnosticReportCustomControlData';
import type {
    IAfwijkingen,
    IOorkV1DiagnosticReportCustomControlData,
} from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1DiagnosticReportCustomControlData';
import { booleanAsJaNee } from '@common/utils/textUtils';
import { FormGroupRead } from '../../../utils/v1/FormGroupRead';

export interface IDiagnosticReportAfwijkingenReadProps {
    customControlData: IOorkV1DiagnosticReportCustomControlData | IOcrkV1DiagnosticReportCustomControlData;
}

const oorkDiagnosticReportCustomControlAfwijkingenLabels: { [key: string]: string } = {
    cataract: 'Cataract',
    glaucoom: 'Glaucoom',
    pseudofaak: 'Pseudofaak',
    md_nat: 'MD nat',
    md_droog: 'MD droog',
    drp: 'DRP',
    amblyopie: 'Amblyopie',
    aangeboren_nystagmus: 'Aangeboren nystagmus',
    verworven_nystagmus: 'Verworven nystagmus',
};

export function DiagnosticReportAfwijkingenRead(props: IDiagnosticReportAfwijkingenReadProps) {
    const { customControlData } = props;
    const afwijkingenList = useMemo(
        () => renderAfwijkingen(customControlData.afwijkingen),
        [customControlData.afwijkingen]
    );

    return (
        <>
            <FormGroupRead
                label="Afwijkingen gevonden"
                value={booleanAsJaNee(customControlData.afwijkingen_gevonden)}
            />

            {customControlData.afwijkingen &&
                Object.values(customControlData.afwijkingen).some((afwijking) => !!afwijking) && (
                    <FormGroup label="Afwijkingen">
                        <List>{afwijkingenList}</List>
                    </FormGroup>
                )}

            {customControlData.afwijkingen.verworven_nystagmus && (
                <FormGroupRead
                    label="Toelichting verworven nystagmus"
                    value={customControlData.toelichting_verworven_nystagmus}
                />
            )}
        </>
    );

    function renderAfwijkingen(afwijkingen: IAfwijkingen) {
        return filterIntoUniqueAfwijkingen(afwijkingen).map((afwijking) => (
            <FormParagraph key={afwijking}>{getAfwijkingenLabel(afwijking)}</FormParagraph>
        ));
    }

    function filterIntoUniqueAfwijkingen(afwijkingen: IAfwijkingen) {
        const filteredAfwijkingen = Object.entries(afwijkingen)
            .filter((afwijking) => afwijking[1])
            .map((afwijking) => afwijking[0].replace(/_text|_o[ds]$/, ''));
        const uniqueAfwijkingen = [...new Set(filteredAfwijkingen)];

        return uniqueAfwijkingen;
    }

    function getAfwijkingenLabel(afwijking: string) {
        const afwijkingLabel = oorkDiagnosticReportCustomControlAfwijkingenLabels[afwijking];

        if (afwijking === 'anders') {
            return customControlData.afwijkingen['anders_text'] || '';
        } else {
            const od = customControlData.afwijkingen[`${afwijking}_od` as keyof IAfwijkingen] ? 'OD' : '';
            const os = customControlData.afwijkingen[`${afwijking}_os` as keyof IAfwijkingen] ? 'OS' : '';

            return `${afwijkingLabel} ${od} ${os}`;
        }
    }
}
